import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography
} from '@mui/material';
import {
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    HealthAndSafety as HealthAndSafetyIcon
} from '@mui/icons-material';
import { initializeDB } from '../../config/db';
import { FALL_ARREST_RESCUE_PLAN_STORE } from '../../config/constants';

/**
 * Модалка выбора FARP для линковки в JHA Step 2.
 *
 * Опции:
 *   - Create new FARP (открывает форму с pre-fill из JHA)
 *   - Выбрать существующий submitted FARP (фильтр: jhaLocalId совпадает,
 *     либо jobCode+planDate, либо последние 7 дней по jobCode)
 *
 * Шаблон полностью повторяет CsepSelectionModal / LttSelectionModal.
 */
export default function FarpSelectionModal({
    open,
    onClose,
    onSelectFarp,
    onCreateNew,
    jhaData,
}) {
    const [farpList, setFarpList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedFarp, setSelectedFarp] = useState(null);

    useEffect(() => {
        if (open) {
            setSelectedFarp(null);
            loadFarps();
        }
    }, [open]);

    const loadFarps = async () => {
        setLoading(true);
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(FALL_ARREST_RESCUE_PLAN_STORE)) {
                const all = await db.getAll(FALL_ARREST_RESCUE_PLAN_STORE);
                setFarpList(all || []);
            } else {
                setFarpList([]);
            }
        } catch (e) {
            console.error('[FarpSelectionModal] failed to load FARPs', e);
            setFarpList([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredFarps = useMemo(() => {
        if (!farpList.length) return [];

        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        const jhaLocalId = jhaData?.localId || '';
        const jhaJobCode = jhaData?.stepOne?.jobNumber || '';
        const jhaDate    = jhaData?.stepOne?.date || '';

        return farpList
            .filter(p => {
                // Только submitted формы — драфты не для линковки.
                if (p.status !== 'submitted') return false;

                // Точное совпадение jhaLocalId — наивысший приоритет.
                if (jhaLocalId && p.jhaLocalId === jhaLocalId) return true;

                // Если уже привязана к ДРУГОЙ JHA — не показываем.
                if (p.jhaLocalId && jhaLocalId && p.jhaLocalId !== jhaLocalId) return false;

                // Совпадение по (jobCode + planDate) — для standalone FARP за этот день.
                if (jhaJobCode && jhaDate && p.jobCode === jhaJobCode && p.planDate === jhaDate) {
                    return true;
                }

                // Fallback: тот же jobCode за последние 7 дней — на случай если юзер
                // подал FARP заранее, а JHA создаёт сегодня.
                if (jhaJobCode && p.jobCode === jhaJobCode) {
                    const submittedAt = p.submittedAt || p.updatedAt || p.createdAt;
                    if (submittedAt) {
                        const t = new Date(submittedAt);
                        if (t >= sevenDaysAgo) return true;
                    }
                }

                return false;
            })
            .sort((a, b) => {
                // Сначала те, что точно привязаны к этой JHA.
                const aLocal = (jhaLocalId && a.jhaLocalId === jhaLocalId) ? 1 : 0;
                const bLocal = (jhaLocalId && b.jhaLocalId === jhaLocalId) ? 1 : 0;
                if (aLocal !== bLocal) return bLocal - aLocal;
                const aT = new Date(a.submittedAt || a.updatedAt || a.createdAt || 0);
                const bT = new Date(b.submittedAt || b.updatedAt || b.createdAt || 0);
                return bT - aT;
            });
    }, [farpList, jhaData]);

    const formatDate = (s) => {
        if (!s) return '—';
        const d = new Date(s);
        return d.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const handleConfirm = () => {
        if (selectedFarp) {
            onSelectFarp(selectedFarp);
            onClose();
        }
    };

    const handleCreate = () => {
        onCreateNew();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { maxHeight: '85vh' } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <HealthAndSafetyIcon color="warning" />
                        <Typography variant="h6">Fall Arrest Rescue Plan</Typography>
                    </Stack>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 2 }}>
                <Button
                    variant="contained"
                    color="warning"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                    fullWidth
                    sx={{ justifyContent: 'flex-start', py: 1.5, mb: 2 }}
                >
                    Create New Fall Arrest Rescue Plan
                </Button>

                <Divider sx={{ my: 2 }}>
                    <Chip label="or link an existing one" size="small" />
                </Divider>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Submitted FARPs matching this JHA (job / date / last 7 days)
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : filteredFarps.length === 0 ? (
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', backgroundColor: '#fafafa' }}>
                        <Typography variant="body2" color="text.secondary">
                            No matching submitted FARPs found
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Create a new one or take a photo
                        </Typography>
                    </Card>
                ) : (
                    <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {filteredFarps.map(p => {
                            const isSelected = selectedFarp?.uuid === p.uuid;
                            const submitter = p.submitterName || p.stepOne?.submitterName || '';
                            const jobCode = p.jobCode || p.stepOne?.jobCode || '';
                            const planDate = p.planDate || p.stepOne?.planDate || '';
                            const isThisJha = jhaData?.localId && p.jhaLocalId === jhaData.localId;
                            return (
                                <Card
                                    key={p.uuid}
                                    variant="outlined"
                                    onClick={() => setSelectedFarp(p)}
                                    sx={{
                                        cursor: 'pointer',
                                        borderColor: isSelected ? 'warning.main' : 'divider',
                                        borderWidth: isSelected ? 2 : 1,
                                        backgroundColor: isSelected ? '#fffbeb' : 'white',
                                        '&:hover': {
                                            borderColor: 'warning.main',
                                            backgroundColor: isSelected ? '#fffbeb' : 'action.hover',
                                        }
                                    }}
                                >
                                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            {isSelected ? (
                                                <CheckCircleIcon color="warning" />
                                            ) : (
                                                <HealthAndSafetyIcon color="disabled" />
                                            )}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 500 }} noWrap>
                                                    {jobCode ? `Job ${jobCode}` : 'No job code'}
                                                    {planDate ? ` · ${planDate}` : ''}
                                                </Typography>
                                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                                    {submitter && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            By: {submitter}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(p.submittedAt || p.updatedAt)}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                            <Stack spacing={0.5} alignItems="flex-end">
                                                {isThisJha && (
                                                    <Chip
                                                        label="This JHA"
                                                        size="small"
                                                        color="warning"
                                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                                    />
                                                )}
                                                <Chip
                                                    label="Submitted"
                                                    size="small"
                                                    color="success"
                                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                                />
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button
                    variant="contained"
                    color="warning"
                    onClick={handleConfirm}
                    disabled={!selectedFarp}
                >
                    Link FARP
                </Button>
            </DialogActions>
        </Dialog>
    );
}
